import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, X } from 'lucide-react';
import api from '../services/api';

type Mailbox = 'inbox' | 'sent';
type StatusFilter = 'all' | 'unread' | 'read' | 'answered';
type SortOrder = 'newest' | 'oldest';

interface Email {
  id: number;
  profile_id: number;
  profile_name: string;
  mailbox?: Mailbox;
  mailbox_label?: string;
  from_email: string;
  from_name: string;
  to_email?: string;
  to_name?: string;
  subject: string;
  date: string;
  seen: boolean;
  answered: boolean;
  size: number;
}

interface InboxError {
  profile_id?: number;
  profile_name?: string;
  message: string;
}

const mailboxLabels: Record<Mailbox, string> = {
  inbox: 'Inbox',
  sent: 'Sent',
};

const allowedTags = new Set([
  'A', 'ABBR', 'B', 'BLOCKQUOTE', 'BR', 'CAPTION', 'CODE', 'COL', 'COLGROUP',
  'DD', 'DIV', 'DL', 'DT', 'EM', 'FONT', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'HR', 'I', 'IMG', 'LI', 'OL', 'P', 'PRE', 'SMALL', 'SPAN', 'STRONG', 'SUB',
  'SUP', 'TABLE', 'TBODY', 'TD', 'TFOOT', 'TH', 'THEAD', 'TR', 'U', 'UL',
]);

const dropWithChildren = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'META', 'LINK', 'BASE']);
const globalAttributes = new Set(['align', 'alt', 'aria-label', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'colspan', 'height', 'rowspan', 'title', 'valign', 'width']);
const uriAttributes = new Set(['href', 'src']);

const isSafeUrl = (value: string, allowDataImage = false) => {
  const trimmed = value.trim();

  if (allowDataImage && /^data:image\/(png|gif|jpe?g|webp);base64,/i.test(trimmed)) {
    return true;
  }

  try {
    const url = new URL(trimmed, window.location.origin);
    return ['http:', 'https:', 'mailto:'].includes(url.protocol);
  } catch (error) {
    return false;
  }
};

const sanitizeEmailHtml = (html: string) => {
  const document = new DOMParser().parseFromString(html, 'text/html');
  const cleanDocument = window.document.implementation.createHTMLDocument('');

  const sanitizeNode = (node: Node, targetParent: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      targetParent.appendChild(cleanDocument.createTextNode(node.textContent || ''));
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toUpperCase();

    if (dropWithChildren.has(tagName)) {
      return;
    }

    if (!allowedTags.has(tagName)) {
      Array.from(element.childNodes).forEach((child) => sanitizeNode(child, targetParent));
      return;
    }

    const cleanElement = cleanDocument.createElement(tagName.toLowerCase());

    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value;

      if (name.startsWith('on') || name === 'style') {
        return;
      }

      if (uriAttributes.has(name)) {
        const allowDataImage = tagName === 'IMG' && name === 'src';

        if (isSafeUrl(value, allowDataImage)) {
          cleanElement.setAttribute(name, value);
        }

        return;
      }

      if (globalAttributes.has(name)) {
        cleanElement.setAttribute(name, value);
      }
    });

    if (tagName === 'A') {
      cleanElement.setAttribute('target', '_blank');
      cleanElement.setAttribute('rel', 'noopener noreferrer');
    }

    Array.from(element.childNodes).forEach((child) => sanitizeNode(child, cleanElement));
    targetParent.appendChild(cleanElement);
  };

  Array.from(document.body.childNodes).forEach((child) => sanitizeNode(child, cleanDocument.body));

  return cleanDocument.body.innerHTML;
};

const Inbox: React.FC = () => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [errors, setErrors] = useState<InboxError[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeMailbox, setActiveMailbox] = useState<Mailbox>('inbox');
  const [accountFilter, setAccountFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [query, setQuery] = useState('');
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [emailBody, setEmailBody] = useState('');
  const [bodyError, setBodyError] = useState('');
  const [loadingBody, setLoadingBody] = useState(false);

  const fetchEmails = useCallback(async (mailbox: Mailbox = activeMailbox) => {
    setLoading(true);
    setSelectedEmail(null);
    setEmailBody('');
    setBodyError('');

    try {
      const response = await api.get('/inbox', { mailbox, limit: 50 });
      const data = response.data;

      if (Array.isArray(data)) {
        setEmails(data);
        setErrors([]);
      } else {
        setEmails(data.emails || []);
        setErrors(data.errors || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch emails', error);
      setEmails([]);
      setErrors([{ message: error.response?.data?.error || error.response?.data?.message || 'Unable to load mailbox.' }]);
    } finally {
      setLoading(false);
    }
  }, [activeMailbox]);

  useEffect(() => {
    fetchEmails(activeMailbox);
  }, [activeMailbox, fetchEmails]);

  const fetchEmailBody = async (email: Email) => {
    setSelectedEmail(email);
    setLoadingBody(true);
    setEmailBody('');
    setBodyError('');

    try {
      const response = await api.get(`/inbox/${email.profile_id}/${email.id}`, {
        mailbox: email.mailbox || activeMailbox,
      });
      setEmailBody(response.data.body || '');
    } catch (error: any) {
      console.error('Failed to fetch email body', error);
      setBodyError(error.response?.data?.error || error.response?.data?.message || 'Unable to load this email.');
    } finally {
      setLoadingBody(false);
    }
  };

  const profiles = useMemo(() => {
    const profileMap = new Map<number, string>();
    emails.forEach((email) => profileMap.set(email.profile_id, email.profile_name));
    return Array.from(profileMap.entries()).map(([id, name]) => ({ id, name }));
  }, [emails]);

  const filteredEmails = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return emails
      .filter((email) => {
        if (accountFilter !== 'all' && String(email.profile_id) !== accountFilter) {
          return false;
        }

        if (statusFilter === 'unread' && email.seen) return false;
        if (statusFilter === 'read' && !email.seen) return false;
        if (statusFilter === 'answered' && !email.answered) return false;

        if (!normalizedQuery) return true;

        return [
          email.subject,
          email.from_name,
          email.from_email,
          email.to_name,
          email.to_email,
          email.profile_name,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery));
      })
      .sort((a, b) => {
        const first = new Date(a.date).getTime() || 0;
        const second = new Date(b.date).getTime() || 0;
        return sortOrder === 'newest' ? second - first : first - second;
      });
  }, [emails, accountFilter, statusFilter, query, sortOrder]);

  const sanitizedEmailBody = useMemo(() => sanitizeEmailHtml(emailBody), [emailBody]);

  const unreadCount = emails.filter((email) => !email.seen).length;
  const readCount = emails.length - unreadCount;
  const answeredCount = emails.filter((email) => email.answered).length;

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleString();
  };

  const formatSize = (bytes: number) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isSelected = (email: Email) => (
    selectedEmail?.id === email.id &&
    selectedEmail?.profile_id === email.profile_id &&
    (selectedEmail?.mailbox || activeMailbox) === (email.mailbox || activeMailbox)
  );

  const displayName = (email: Email) => {
    if (activeMailbox === 'sent') {
      return email.to_name || email.to_email || 'No recipient';
    }

    return email.from_name || email.from_email || 'Unknown sender';
  };

  const displayAddress = (email: Email) => (
    activeMailbox === 'sent' ? email.to_email : email.from_email
  );

  const resetFilters = () => {
    setAccountFilter('all');
    setStatusFilter('all');
    setSortOrder('newest');
    setQuery('');
  };

  const inputStyle: React.CSSProperties = {
    height: '38px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    background: '#ffffff',
    color: '#111827',
    fontSize: '0.875rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1600px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', margin: '0 0 4px 0' }}>
            Mailbox
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
            Review incoming replies and sent Gmail messages from connected IMAP accounts
          </p>
        </div>

        <button
          onClick={() => fetchEmails()}
          disabled={loading}
          title="Refresh mailbox"
          style={{
            height: '38px',
            padding: '0 14px',
            background: loading ? '#e5e7eb' : '#2563eb',
            color: loading ? '#6b7280' : '#ffffff',
            border: 'none',
            borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {(['inbox', 'sent'] as Mailbox[]).map((mailbox) => (
          <button
            key={mailbox}
            onClick={() => setActiveMailbox(mailbox)}
            style={{
              height: '36px',
              padding: '0 16px',
              border: activeMailbox === mailbox ? '1px solid #2563eb' : '1px solid #d1d5db',
              borderRadius: '8px',
              background: activeMailbox === mailbox ? '#eff6ff' : '#ffffff',
              color: activeMailbox === mailbox ? '#1d4ed8' : '#374151',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {mailboxLabels[mailbox]}
          </button>
        ))}
      </div>

      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1.5fr) repeat(3, minmax(150px, 0.8fr)) auto', gap: '12px', padding: '14px', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '11px', color: '#6b7280' }} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search subject, sender, recipient"
              style={{ ...inputStyle, width: '100%', padding: '0 38px 0 36px' }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                title="Clear search"
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '7px',
                  width: '24px',
                  height: '24px',
                  border: 'none',
                  background: 'transparent',
                  color: '#6b7280',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>

          <select
            value={accountFilter}
            onChange={(event) => setAccountFilter(event.target.value)}
            style={{ ...inputStyle, width: '100%', padding: '0 12px' }}
          >
            <option value="all">All accounts</option>
            {profiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            style={{ ...inputStyle, width: '100%', padding: '0 12px' }}
          >
            <option value="all">All status</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
            <option value="answered">Answered</option>
          </select>

          <select
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value as SortOrder)}
            style={{ ...inputStyle, width: '100%', padding: '0 12px' }}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>

          <button
            onClick={resetFilters}
            style={{
              height: '38px',
              padding: '0 14px',
              background: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            Reset
          </button>
        </div>

        <div style={{ display: 'flex', gap: '18px', padding: '0 14px 14px', color: '#6b7280', fontSize: '0.75rem' }}>
          <span>{emails.length} loaded</span>
          <span>{filteredEmails.length} shown</span>
          <span>{unreadCount} unread</span>
          <span>{readCount} read</span>
          <span>{answeredCount} answered</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedEmail ? 'minmax(0, 0.95fr) minmax(360px, 1.05fr)' : 'minmax(0, 1fr)', gap: '16px' }}>
        <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: 0 }}>
              {mailboxLabels[activeMailbox]}
            </h2>
            <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>
              {filteredEmails.length} messages
            </span>
          </div>

          {errors.length > 0 && (
            <div style={{ padding: '16px', background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
              {errors.map((error, index) => (
                <div key={`${error.profile_id || 'mailbox'}-${index}`} style={{ color: '#991b1b', fontSize: '0.875rem', lineHeight: 1.5, marginBottom: index === errors.length - 1 ? 0 : '8px' }}>
                  <strong>{error.profile_name || mailboxLabels[activeMailbox]}:</strong> {error.message}
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
              Loading {mailboxLabels[activeMailbox].toLowerCase()}...
            </div>
          ) : filteredEmails.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '1rem', color: '#374151', fontWeight: 600 }}>No messages found</p>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                {emails.length === 0 ? 'Make sure IMAP is enabled in sender profiles.' : 'Try changing the filters or search term.'}
              </p>
            </div>
          ) : (
            <div style={{ maxHeight: '650px', overflowY: 'auto' }}>
              {filteredEmails.map((email) => (
                <div
                  key={`${email.mailbox || activeMailbox}-${email.profile_id}-${email.id}`}
                  onClick={() => fetchEmailBody(email)}
                  style={{
                    padding: '14px 16px',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    background: isSelected(email) ? '#eff6ff' : 'white',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(event) => {
                    if (!isSelected(email)) {
                      event.currentTarget.style.background = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(event) => {
                    if (!isSelected(email)) {
                      event.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '12px' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', minWidth: 0 }}>
                        {!email.seen && (
                          <span style={{ width: '8px', height: '8px', background: '#2563eb', borderRadius: '50%', display: 'inline-block', flexShrink: 0 }} />
                        )}
                        <span style={{ fontWeight: email.seen ? 500 : 700, color: '#111827', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {displayName(email)}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '5px', fontWeight: email.seen ? 400 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {email.subject || '(No subject)'}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', color: '#6b7280', fontSize: '0.75rem', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayAddress(email)}</span>
                        <span>{email.profile_name}</span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      <div style={{ marginBottom: '5px' }}>{formatDate(email.date)}</div>
                      <div style={{ color: '#9ca3af' }}>{formatSize(email.size)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedEmail && (
          <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                Message
              </h2>
              <button
                onClick={() => setSelectedEmail(null)}
                style={{
                  height: '32px',
                  padding: '0 12px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                Close
              </button>
            </div>

            <div style={{ padding: '16px' }}>
              <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>From:</span>{' '}
                  <span style={{ fontSize: '0.875rem', color: '#111827' }}>
                    {selectedEmail.from_name || selectedEmail.from_email} &lt;{selectedEmail.from_email}&gt;
                  </span>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>To:</span>{' '}
                  <span style={{ fontSize: '0.875rem', color: '#111827' }}>
                    {selectedEmail.to_name || selectedEmail.to_email || 'No recipient'}{selectedEmail.to_email ? ` <${selectedEmail.to_email}>` : ''}
                  </span>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>Subject:</span>{' '}
                  <span style={{ fontSize: '0.875rem', color: '#111827', fontWeight: 600 }}>
                    {selectedEmail.subject || '(No subject)'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>Date:</span>{' '}
                    <span style={{ fontSize: '0.875rem', color: '#111827' }}>{formatDate(selectedEmail.date)}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>Account:</span>{' '}
                    <span style={{ fontSize: '0.875rem', color: '#111827' }}>{selectedEmail.profile_name}</span>
                  </div>
                </div>
              </div>

              {loadingBody ? (
                <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
                  Loading message...
                </div>
              ) : bodyError ? (
                <div style={{ padding: '24px', background: '#fef2f2', borderRadius: '8px', color: '#991b1b', fontSize: '0.875rem', lineHeight: 1.5 }}>
                  {bodyError}
                </div>
              ) : emailBody ? (
                <div
                  style={{
                    padding: '16px',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    maxHeight: '520px',
                    overflowY: 'auto',
                    fontSize: '0.875rem',
                    lineHeight: '1.6',
                    color: '#374151',
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizedEmailBody }}
                />
              ) : (
                <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
                  No message body available
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inbox;
